import React, { useEffect } from 'react';
import { useIntl } from 'react-intl';
import { showSlidingDoorsInfoSheet } from 'ducks/sheets';
import { hasInvalidDoorPlacement, setInvalidSlidingDoorPlacementInfo } from 'ducks/layout';
import { useSelector } from 'react-redux';
import { getSelectedProduct } from 'ducks/products';
import { useCore } from 'contexts/core';
import { isMobile } from 'utils/platform';
import media from 'ui/media';
import styled from 'styled-components';
import usePrevious from 'hooks/usePrevious';
import { useAppDispatch } from 'ducks/hooks';
import SkapaInlineMessage, { InlineMessageVariantEnum } from 'ui/InlineMessage/SkapaInlineMessage';
import InformationCircle from '@ingka/ssr-icon/paths/information-circle';
import { useBehaviorTracker } from 'contexts/behaviors';

const Box = styled.div`
  padding: ${({ theme }) => theme.space.xs} 0 ${({ theme }) => theme.space.s} 0;
  ${media.mobile`
   padding: 1.5rem 1.5rem 0 1.5rem;
  `}
`;

const SlidingDoorsInfoMessage = () => {
  const { formatMessage } = useIntl();
  const coreApi = useCore();
  const messageRef = React.useRef<HTMLInputElement>(null);

  const selectedProduct = useSelector(getSelectedProduct);
  const selectedEntity = selectedProduct ? coreApi.entities.find(selectedProduct.entityId) : null;

  const invalidDoorPlacement = useSelector(hasInvalidDoorPlacement);
  const previousInvalidDoorPlacement = usePrevious(invalidDoorPlacement);

  const infoText = invalidDoorPlacement
    ? formatMessage({ id: 'storageone.slidingDoorsWarningText' })
    : formatMessage({ id: 'storageone.slidingDoorsInfoSubtitle' });

  const dispatch = useAppDispatch();
  const selectedEntityPresent = !!selectedEntity;

  const behaviorTracker = useBehaviorTracker();

  useEffect(() => {
    if (selectedEntityPresent) {
      dispatch(setInvalidSlidingDoorPlacementInfo(false));
    }
  }, [dispatch, selectedEntityPresent]);

  if (invalidDoorPlacement && !previousInvalidDoorPlacement) {
    messageRef.current?.scrollIntoView({ block: 'center', behavior: 'smooth' });
  }

  const openSlidingDoorsInfoSheet = () =>
    behaviorTracker.custom(() => dispatch(showSlidingDoorsInfoSheet()));

  return (
    <Box ref={messageRef}>
      <SkapaInlineMessage
        title={
          invalidDoorPlacement
            ? formatMessage({ id: 'storageone.slidingDoorsWarning' })
            : formatMessage({ id: 'storageone.slidingDoorsInfoTitle' })
        }
        body={infoText}
        variant={
          invalidDoorPlacement
            ? InlineMessageVariantEnum.NEGATIVE
            : InlineMessageVariantEnum.INFORMATIVE
        }
        ssrIcon={InformationCircle}
        subtle={isMobile() ? true : false}
        actions={[
          {
            text: formatMessage({ id: 'storageone.slidingDoorsInfoButton' }),
            onClick: openSlidingDoorsInfoSheet
          }
        ]}
      />
    </Box>
  );
};
export default SlidingDoorsInfoMessage;
